/**
 * HideCategories — lets the user pick which Live / Movie / Series categories
 * to hide from Browse.
 *
 * Reads the live playlist channels from PlaylistDataContext so the list of
 * available categories is always accurate (no static seed list). Persists
 * picks via lib/hiddenCategories.js, keyed by (playlistId × content_type).
 *
 * Inspired by VU Player Pro's MobileHideCategoryActivity. Categories the
 * user toggles off will not appear in the Browse sidebar, search results,
 * or section counts on the home tile grid.
 */
import React, { useMemo, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { usePlaylistData } from "../lib/PlaylistDataContext";
import {
  getHiddenCategories,
  toggleHiddenCategory,
  clearHiddenCategories,
} from "../lib/hiddenCategories";
import {
  ArrowLeft,
  Eye,
  EyeOff,
  Tv,
  Film,
  Clapperboard,
  RotateCcw,
  Search,
} from "lucide-react";

const TYPES = [
  { key: "live_tv", label: "Live TV", icon: Tv },
  { key: "movie", label: "Movies", icon: Film },
  { key: "series", label: "Series", icon: Clapperboard },
];

export default function HideCategories() {
  const navigate = useNavigate();
  const outletCtx = useOutletContext() || {};
  const playlistId = outletCtx.activePlaylistId || "default";
  const { channels, status } = usePlaylistData();
  const [activeType, setActiveType] = useState("live_tv");
  const [query, setQuery] = useState("");
  // Re-render trigger when toggling
  const [tick, setTick] = useState(0);

  // Build category list for the current type from live playlist data
  const categories = useMemo(() => {
    const seen = new Map();
    for (const ch of channels || []) {
      if (ch.content_type !== activeType) continue;
      const cat = ch.group_title || "Uncategorized";
      seen.set(cat, (seen.get(cat) || 0) + 1);
    }
    return Array.from(seen.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([name, count]) => ({ name, count }));
  }, [channels, activeType]);

  const hidden = useMemo(
    () => getHiddenCategories(playlistId, activeType),
    // tick forces recompute after toggle
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [playlistId, activeType, tick]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return categories;
    return categories.filter((c) => c.name.toLowerCase().includes(q));
  }, [categories, query]);

  const onToggle = (name) => {
    toggleHiddenCategory(playlistId, activeType, name);
    setTick((t) => t + 1);
  };

  const onShowAll = () => {
    if (!hidden.size) return;
    if (!window.confirm(`Show all ${TYPES.find((t) => t.key === activeType)?.label} categories again?`)) return;
    clearHiddenCategories(playlistId, activeType);
    setTick((t) => t + 1);
  };

  return (
    <div data-testid="player-hide-categories" className="space-y-5 max-w-3xl">
      <button
        onClick={() => navigate("/player/settings")}
        className="flex items-center gap-1.5 text-sm text-white/60 hover:text-white transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Settings
      </button>

      <div>
        <h2 className="text-xl font-bold tracking-tight">Hide categories</h2>
        <p className="text-sm text-white/60 mt-1">
          Pick categories you don&apos;t want to see in Live TV, Movies or Series.
          Hidden categories are removed from the sidebar, search results and
          home counts on this device.
        </p>
      </div>

      {/* Type tabs */}
      <div className="flex gap-2">
        {TYPES.map((t) => {
          const Icon = t.icon;
          const active = activeType === t.key;
          return (
            <button
              key={t.key}
              data-testid={`hide-tab-${t.key}`}
              onClick={() => {
                setActiveType(t.key);
                setQuery("");
              }}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                active
                  ? "bg-[#f50261]/15 border-[#f50261]/40 text-white"
                  : "bg-white/5 border-white/10 text-white/70 hover:text-white hover:bg-white/10"
              }`}
            >
              <Icon className="w-4 h-4" />
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Search + bulk reset */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
          <input
            data-testid="hide-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Filter categories…"
            className="w-full bg-black/40 border border-white/10 rounded-lg pl-9 pr-3 py-2 text-sm text-white placeholder:text-white/40 focus:outline-none focus:border-[#f50261]/50"
          />
        </div>
        <button
          onClick={onShowAll}
          disabled={!hidden.size}
          data-testid="hide-show-all-btn"
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <RotateCcw className="w-3.5 h-3.5" />
          Show all
        </button>
      </div>

      {/* Counts pill */}
      <div className="text-xs text-white/60">
        {status === "loading" ? (
          "Loading playlist…"
        ) : (
          <>
            <b className="text-white">{categories.length}</b> categories ·{" "}
            <b className="text-[#ff4d9d]">{hidden.size}</b> hidden
          </>
        )}
      </div>

      {/* Category list */}
      <div className="bg-[#11141b] border border-white/5 rounded-2xl divide-y divide-white/5 overflow-hidden">
        {filtered.length === 0 ? (
          <div className="px-4 py-10 text-center text-sm text-white/50">
            {status === "loading"
              ? "Waiting for the playlist to finish loading…"
              : query
              ? "No categories match that filter."
              : "No categories found for this section yet."}
          </div>
        ) : (
          filtered.map((c) => {
            const isHidden = hidden.has(c.name);
            return (
              <button
                key={c.name}
                data-testid={`hide-row-${c.name}`}
                onClick={() => onToggle(c.name)}
                className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                  isHidden ? "bg-[#f50261]/5 hover:bg-[#f50261]/10" : "hover:bg-white/5"
                }`}
              >
                <div
                  className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
                    isHidden ? "bg-[#f50261]/15 text-[#ff4d9d]" : "bg-white/5 text-white/60"
                  }`}
                >
                  {isHidden ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className={`text-sm font-medium truncate ${isHidden ? "text-white/70 line-through" : "text-white"}`}>
                    {c.name}
                  </div>
                  <div className="text-[11px] text-white/40">{c.count.toLocaleString()} items</div>
                </div>
                <div
                  className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded-md ${
                    isHidden ? "bg-[#f50261]/20 text-[#ff4d9d]" : "bg-emerald-400/15 text-emerald-300"
                  }`}
                >
                  {isHidden ? "Hidden" : "Visible"}
                </div>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}
